import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { openIncident } from "@/pages/manufacturing/qcIncidentsApi";
import { linkFinalQcGpsCheckQcIncident } from "@/pages/manufacturing/finalQcApi";

export function FinalQcGpsIncidentModal({
  open,
  serial,
  attemptNumber,
  failureModeId,
  failureModeName,
  onClose,
  onSubmitted,
}: {
  open: boolean;
  serial: string | null;
  attemptNumber: number;
  failureModeId: number | null;
  failureModeName: string | null;
  onClose: () => void;
  onSubmitted: () => void;
}): JSX.Element | null {
  const [issue, setIssue] = useState("");
  const [issueNotes, setIssueNotes] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || serial == null) return;
    const modeLabel = failureModeName != null && failureModeName.trim() !== "" ? failureModeName : "GPS check failed";
    setIssue(`${modeLabel} (check ${attemptNumber}) — ${serial}`);
    setIssueNotes(`Final QC GPS check — attempt ${attemptNumber}.`);
    setError(null);
  }, [open, serial, attemptNumber, failureModeName]);

  if (!open || serial == null) return null;

  const submit = async () => {
    const trimmedIssue = issue.trim();
    if (!trimmedIssue) {
      setError("Enter a short issue summary.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      const incident = await openIncident({
        type: "device",
        serial,
        issue: trimmedIssue,
        issue_notes: issueNotes.trim() || null,
        failureModeIds: failureModeId != null ? [failureModeId] : undefined,
      });
      await linkFinalQcGpsCheckQcIncident(serial, incident.id);
      onSubmitted();
      onClose();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : String(submitError));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-lg shadow-lg">
        <CardHeader>
          <h2 className="text-lg font-semibold">Log QC incident</h2>
          <p className="text-sm text-muted-foreground">
            Final QC GPS check failed on check {attemptNumber}. Submit an incident or ask a manufacturing
            administrator for an override.
            {failureModeName != null && failureModeName.trim() !== ""
              ? ` This incident will be linked to ${failureModeName}.`
              : ""}
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <Label htmlFor="final-qc-gps-qc-issue">Issue (short summary)</Label>
            <Input
              id="final-qc-gps-qc-issue"
              value={issue}
              onChange={(event) => setIssue(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="final-qc-gps-qc-notes">Issue notes</Label>
            <textarea
              id="final-qc-gps-qc-notes"
              className="mt-1 min-h-[120px] w-full rounded border px-2 py-1 text-sm"
              value={issueNotes}
              onChange={(event) => setIssueNotes(event.target.value)}
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button type="button" disabled={pending} onClick={() => void submit()}>
              {pending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                "Submit incident"
              )}
            </Button>
            <Button type="button" variant="secondary" disabled={pending} onClick={onClose}>
              Close
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
